import { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { getSocket } from '@/services/socket';
import type { ConnectedUser } from '@the-wall/shared';
import { UserAvatar } from '@/components/UserAvatar';

interface LiveUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function LiveIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z"
      />
    </svg>
  );
}

function UserRow({ user }: { user: ConnectedUser }) {
  return (
    <li className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-wall-border/20 transition-colors">
      <UserAvatar userId={user.id} username={user.username} className="h-10 w-10" />
      <div className="flex-1 min-w-0">
        <p className="text-wall-text font-medium truncate">{user.username}</p>
        <p className="text-wall-muted text-sm truncate">{user.email}</p>
      </div>
    </li>
  );
}

export function LiveUsersModal({ isOpen, onClose }: LiveUsersModalProps) {
  const [users, setUsers] = useState<ConnectedUser[]>([]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    const socket = getSocket();
    if (socket) {
      socket.emit('live:request');

      const onLiveUsers = (data: { users: ConnectedUser[] }) => {
        setUsers(data.users);
      };
      const onLiveUserJoined = (data: ConnectedUser) => {
        setUsers((prev) => {
          const i = prev.findIndex((u) => u.id === data.id);
          if (i >= 0) return prev.map((u, j) => (j === i ? data : u));
          return [...prev, data];
        });
      };
      const onLiveUserLeft = (data: { userId: string }) => {
        setUsers((prev) => prev.filter((u) => u.id !== data.userId));
      };

      socket.on('live:users', onLiveUsers);
      socket.on('live:userJoined', onLiveUserJoined);
      socket.on('live:userLeft', onLiveUserLeft);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
        socket.off('live:users', onLiveUsers);
        socket.off('live:userJoined', onLiveUserJoined);
        socket.off('live:userLeft', onLiveUserLeft);
      };
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md min-h-full"
        onClick={handleClose}
        aria-hidden
      />
      <div
        className="relative bg-wall-surface border border-wall-border rounded-2xl shadow-2xl w-full max-w-sm max-h-[85dvh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-4 pt-4 pb-3 border-b border-wall-border/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden />
            <LiveIcon className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-wall-text tracking-tight">
              Live now
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 -mr-2 text-wall-muted hover:text-wall-text rounded-lg hover:bg-wall-border/30 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-4 py-2 text-wall-muted text-sm border-b border-wall-border/50">
          {users.length} {users.length === 1 ? 'user' : 'users'} on the canvas right now
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
          {users.length === 0 ? (
            <p className="text-wall-muted text-sm py-6 text-center">
              No one else here yet. Only logged-in users appear in this list.
            </p>
          ) : (
            <ul className="divide-y divide-wall-border/50">
              {users.map((user) => (
                <UserRow key={user.id} user={user} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
